import React from 'react';
import { VolumeData } from '../types';
import { useFormatters } from '../hooks/useVolumeData';

interface VolumeCardProps {
  data: VolumeData;
  icon?: string;
  color?: string;
}

export const VolumeCard: React.FC<VolumeCardProps> = ({ 
  data, 
  icon = '/images/usdc.png',
  color = 'blue'
}) => {
  const { formatVolume, formatNumber } = useFormatters();

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'green':
        return {
          gradient: 'from-green-400 to-emerald-500',
          border: 'border-green-500/20',
          bg: 'bg-green-500/10'
        };
      case 'purple':
        return {
          gradient: 'from-purple-400 to-violet-500',
          border: 'border-purple-500/20',
          bg: 'bg-purple-500/10'
        };
      case 'orange':
        return {
          gradient: 'from-orange-400 to-amber-500',
          border: 'border-orange-500/20',
          bg: 'bg-orange-500/10'
        };
      default:
        return {
          gradient: 'from-blue-400 to-cyan-500',
          border: 'border-blue-500/20',
          bg: 'bg-blue-500/10'
        };
    }
  };

  const colorClasses = getColorClasses(color);

  return (
    <div className={`volume-card ${colorClasses.border} ${colorClasses.bg}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {data.token !== 'TOTAL' && (
            <img 
              src={icon} 
              alt={data.token} 
              className="w-8 h-8 rounded-full"
              onError={(e) => {
                e.currentTarget.src = '/images/wshido.png';
              }}
            />
          )}
          <h3 className="text-lg font-semibold text-white">
            {data.token === 'TOTAL' ? 'Total Volume' : data.token}
          </h3>
        </div>
        
        {data.token === 'TOTAL' && (
          <div className="flex items-center space-x-1">
            <img src="/images/usdc.png" alt="USDC" className="w-5 h-5 rounded-full" />
            <img src="/images/usdt.png" alt="USDT" className="w-5 h-5 rounded-full" />
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Total Volume */}
        <div className="text-center">
          <div className={`text-3xl font-bold bg-gradient-to-r ${colorClasses.gradient} bg-clip-text text-transparent`}>
            {formatVolume(data.totalVolume)}
          </div>
          <div className="text-sm text-gray-400 mt-1">Total Volume</div>
        </div>

        {/* Mint/Burn Breakdown */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-xl font-semibold text-green-400">
              {formatVolume(data.mintVolume)}
            </div>
            <div className="text-xs text-gray-400">Mint (In)</div>
            <div className="text-xs text-gray-500">
              {formatNumber(data.mintCount)} txs
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-xl font-semibold text-red-400">
              {formatVolume(data.burnVolume)}
            </div>
            <div className="text-xs text-gray-400">Burn (Out)</div>
            <div className="text-xs text-gray-500">
              {formatNumber(data.burnCount)} txs
            </div>
          </div>
        </div>

        {/* Net Flow */}
        <div className="text-center pt-2 border-t border-gray-700/50">
          <div className={`text-lg font-semibold ${
            parseFloat(data.netFlow) >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {parseFloat(data.netFlow) >= 0 ? '+' : ''}{formatVolume(data.netFlow)}
          </div>
          <div className="text-xs text-gray-400">Net Flow</div>
        </div>

        {/* Transaction Count */}
        <div className="text-center">
          <div className="text-sm text-gray-300">
            {formatNumber(data.totalCount)} total transactions
          </div>
        </div>
      </div>
    </div>
  );
};
